/**
 * LienTab — Modal Tab 2: California lien intelligence.
 * canonical: S2 lines 1667–1705 (.pm-section-h, panel rows, Soren blurb)
 *
 * Live data: fetchProjectLienAlerts + project.completed_at windows
 * Saving completion recomputes mechanics (+90) and retention (+45) dates.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  fetchProjectLienAlerts,
  downloadAriaLienNoticePdf,
  fetchProject,
  setProjectCompletionDate,
} from '../../../api/projects'
import PreLienInstructionsModal from '../../../components/PreLienInstructionsModal'
import ComingSoonModal from '../../../components/ComingSoonModal'
import LienDocsList from './LienDocsList'
import { humanizeDaysRemaining } from '../../../features/soren/humanizeSorenDates'
import { useActionToast } from '../../../lib/useActionToast'

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (m) {
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
      .toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })
  }
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'numeric', day: 'numeric', year: 'numeric',
  })
}

function toInputDate(iso: string | null | undefined): string {
  if (!iso) return ''
  const m = String(iso).match(/^(\d{4}-\d{2}-\d{2})/)
  return m ? m[1] : ''
}

function addDaysIso(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number)
  const dt = new Date(y, m - 1, d)
  dt.setDate(dt.getDate() + days)
  const yy = dt.getFullYear()
  const mm = String(dt.getMonth() + 1).padStart(2, '0')
  const dd = String(dt.getDate()).padStart(2, '0')
  return `${yy}-${mm}-${dd}`
}

function daysLeftNonNeg(deadlineIso: string, now = new Date()): number {
  const [y, m, d] = deadlineIso.split('-').map(Number)
  const deadline = new Date(y, m - 1, d)
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const rem = Math.round((deadline.getTime() - today.getTime()) / 86400000)
  return Math.max(0, rem)
}

function isPreLienType(t: string | null | undefined) {
  return t === 'pre_lien' || t === 'preliminary_20day'
}
function isMechanicsType(t: string | null | undefined) {
  return t === 'mechanics_lien' || t === 'filing_deadline'
}
function isRetentionType(t: string | null | undefined) {
  return t === 'retention_release'
}

interface Props { projectId: number }

export default function LienTab({ projectId }: Props) {
  const qc = useQueryClient()
  const { showToast, Toast } = useActionToast()
  const [showInstructions, setShowInstructions] = useState(false)
  const [showMailModal, setShowMailModal] = useState(false)
  const [pdfError, setPdfError] = useState<string | null>(null)
  const [dateInput, setDateInput] = useState('')
  const [saveErr, setSaveErr] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [hasConfirmedSave, setHasConfirmedSave] = useState(false)
  const saveInFlight = useRef(false)

  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => fetchProject(projectId),
    staleTime: 30_000,
  })

  const { data: alerts = [], isPending } = useQuery({
    queryKey: ['project-lien-alerts', projectId],
    queryFn: () => fetchProjectLienAlerts(projectId),
    staleTime: 60_000,
  })

  const completedAt = project?.completed_at ?? null

  useEffect(() => {
    setDateInput(toInputDate(completedAt))
    if (completedAt) setHasConfirmedSave(true)
    setEditing(false)
  }, [completedAt])

  const windows = useMemo(() => {
    const iso = toInputDate(completedAt)
    if (!iso) {
      return {
        mechanics: null as string | null,
        retention: null as string | null,
        mechanicsDays: null as number | null,
        retentionDays: null as number | null,
      }
    }
    const mechanics = addDaysIso(iso, 90)
    const retention = addDaysIso(iso, 45)
    return {
      mechanics,
      retention,
      mechanicsDays: daysLeftNonNeg(mechanics),
      retentionDays: daysLeftNonNeg(retention),
    }
  }, [completedAt])

  const savedIso = toInputDate(completedAt)
  const dirty = dateInput !== savedIso
  const showSavedView = hasConfirmedSave && !editing && !dirty

  const outstanding = parseFloat(String(project?.outstanding ?? '0')) || 0
  const paidInFull = outstanding <= 0.009

  const lienStatus = useMemo(() => {
    if (paidInFull) return 'No lien filed — none needed, paid in full'
    if (!windows.mechanics) return 'Mechanics lien window — set completion date to track'
    const n = windows.mechanicsDays ?? 0
    const label = fmtDate(windows.mechanics)
    if (n === 0) return `Mechanics lien window closes ${label} (today)`
    return `Mechanics lien window closes ${label} (${n} day${n === 1 ? '' : 's'})`
  }, [paidInFull, windows])

  const preFiledAlert = alerts.find(a => isPreLienType(a.notice_type) && a.filed_at)
  const mechanicsAlert = alerts.find(a => isMechanicsType(a.notice_type))
  const retentionAlert = alerts.find(a => isRetentionType(a.notice_type))
  const preLienAlert = alerts.find(a => isPreLienType(a.notice_type))

  const saveMutation = useMutation({
    mutationFn: (value: string | null) => setProjectCompletionDate(projectId, value),
    onSuccess: async (_data, value) => {
      setSaveErr(null)
      setEditing(false)
      setHasConfirmedSave(true)
      if (value) {
        const closes = addDaysIso(value, 90)
        showToast(`Completion date saved — mechanics lien window closes ${fmtDate(closes)}`)
      } else {
        showToast('Completion date cleared — Not yet')
      }
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['project', projectId] }),
        qc.invalidateQueries({ queryKey: ['projects'] }),
        qc.invalidateQueries({ queryKey: ['project-lien-alerts', projectId] }),
        qc.invalidateQueries({ queryKey: ['aria-cash-intel'] }),
      ])
    },
    onError: (e: Error) => setSaveErr(e.message || 'Save failed'),
    onSettled: () => {
      saveInFlight.current = false
    },
  })

  async function handlePdfDownload() {
    setPdfError(null)
    try {
      await downloadAriaLienNoticePdf(projectId)
    } catch (e: unknown) {
      setPdfError(e instanceof Error ? e.message : 'PDF download failed')
    }
  }

  function handleSaveDate() {
    if (saveInFlight.current || saveMutation.isPending) return
    const v = dateInput.trim()
    const next = v || null
    const prev = savedIso || null
    if (next === prev) return
    saveInFlight.current = true
    saveMutation.mutate(next)
  }

  function handleClearDate() {
    if (saveInFlight.current || saveMutation.isPending) return
    if (!savedIso && !dateInput) return
    saveInFlight.current = true
    setDateInput('')
    saveMutation.mutate(null)
  }

  const saveDisabled =
    saveMutation.isPending || saveInFlight.current || !dirty

  const mechanicsDeadline = windows.mechanics || mechanicsAlert?.deadline_date || null
  const retentionDeadline = windows.retention || retentionAlert?.deadline_date || null
  const mechanicsDays = windows.mechanicsDays
    ?? (mechanicsAlert?.days_remaining != null
      ? Math.max(0, mechanicsAlert.days_remaining)
      : null)
  const retentionDays = windows.retentionDays
    ?? (retentionAlert?.days_remaining != null
      ? Math.max(0, retentionAlert.days_remaining)
      : null)

  return (
    <div>
      {Toast}
      <div className="pm-section-h">
        <div>
          <div className="pm-section-tt">California lien intelligence</div>
          <div className="pm-section-st">Soren tracks every deadline · Civ Code §8200</div>
        </div>
      </div>

      {/* Completion date */}
      <div
        className="panel"
        style={{ marginBottom: 14, padding: '12px 16px' }}
        data-testid="lien-completion-panel"
      >
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink)', marginBottom: 6 }}>
          Project completed on
        </div>
        {showSavedView ? (
          <div
            style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}
            data-testid="lien-completion-saved"
          >
            <span
              style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}
              data-testid="lien-completion-saved-value"
            >
              {savedIso ? fmtDate(savedIso) : 'Not yet'}
            </span>
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: 'var(--green)',
                background: 'var(--green-l)',
                borderRadius: 6,
                padding: '3px 7px',
              }}
              data-testid="lien-completion-saved-badge"
            >
              Saved
            </span>
            <button
              type="button"
              className="btn b-ol btn-sm"
              data-testid="lien-completion-edit"
              onClick={() => setEditing(true)}
            >
              Edit
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="date"
              className="fld-i"
              value={dateInput}
              onChange={(e) => {
                setDateInput(e.target.value)
                setEditing(true)
              }}
              data-testid="lien-completion-date"
              style={{ maxWidth: 180 }}
              aria-label="Project completed on"
            />
            <button
              type="button"
              className="btn b-dk btn-sm"
              data-testid="lien-completion-save"
              disabled={saveDisabled}
              onClick={handleSaveDate}
            >
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className="btn b-ol btn-sm"
              data-testid="lien-completion-clear"
              disabled={saveMutation.isPending || (!completedAt && !dateInput)}
              onClick={handleClearDate}
            >
              Not yet
            </button>
          </div>
        )}
        {saveErr && (
          <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 6 }}>{saveErr}</div>
        )}
        <div
          style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 8, lineHeight: 1.45 }}
          data-testid="lien-status-line"
        >
          {lienStatus}
        </div>
      </div>

      {isPending && (
        <div style={{ padding: '18px 0', fontSize: 12, color: 'var(--ink4)' }}>
          Loading lien data…
        </div>
      )}

      {!isPending && (
        <div className="panel" style={{ marginBottom: 14 }}>
          <div style={{
            padding: '14px 16px',
            display: 'grid',
            gridTemplateColumns: '40px 1fr auto',
            gap: 14,
            alignItems: 'center',
            borderBottom: '1px solid var(--line)',
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: 6,
              background: preFiledAlert ? 'var(--green-l)' : 'var(--accent-l)',
              color: preFiledAlert ? 'var(--green)' : 'var(--ink3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16, fontWeight: 700,
            }}>
              {preFiledAlert ? '✓' : '○'}
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink)' }}>
                20-Day Pre Lien Notice
              </div>
              <div style={{ fontSize: 11, color: 'var(--ink4)', marginTop: 2 }}>
                {preFiledAlert
                  ? `Filed ${fmtDate(preFiledAlert.filed_at)} · certified mail`
                  : 'Not yet filed · Soren can draft and send via Lob.com'}
              </div>
              {pdfError && (
                <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 4 }}>
                  {pdfError}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <button
                className="btn b-dk btn-sm"
                onClick={handlePdfDownload}
                data-testid="lien-tab-pdf-btn"
              >
                ↓ PDF
              </button>
              <button
                className="btn b-ol btn-sm"
                onClick={() => setShowInstructions(true)}
                data-testid="lien-tab-instructions-btn"
              >
                Instructions
              </button>
              <button
                className="btn b-ol btn-sm"
                onClick={() => setShowMailModal(true)}
                data-testid="lien-tab-mail-btn"
              >
                Mail for me
              </button>
            </div>
          </div>

          <div style={{
            padding: '14px 16px', display: 'grid',
            gridTemplateColumns: '40px 1fr auto', gap: 14, alignItems: 'center',
            borderBottom: '1px solid var(--line)',
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: 6,
              background: 'var(--amber-l)', color: 'var(--amber)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16, fontWeight: 700,
            }}>
              ⏰
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink)' }}>
                Mechanics Lien deadline
              </div>
              <div
                style={{ fontSize: 11, color: 'var(--ink4)', marginTop: 2 }}
                data-testid="lien-mechanics-row"
              >
                {mechanicsDeadline
                  ? <>90 days from project completion ·{' '}
                      <b style={{ color: 'var(--ink)' }}>{fmtDate(mechanicsDeadline)}</b>
                      {mechanicsDays != null
                        && ` · ${humanizeDaysRemaining(mechanicsDays, mechanicsDeadline)}`}
                    </>
                  : '90 days from project completion · set completion date above'}
              </div>
            </div>
            <span className="chip c-amb">Tracking</span>
          </div>

          <div style={{
            padding: '14px 16px', display: 'grid',
            gridTemplateColumns: '40px 1fr auto', gap: 14, alignItems: 'center',
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: 6,
              background: 'var(--accent-l)', color: 'var(--ink3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16, fontWeight: 700,
            }}>
              $
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink)' }}>
                Retention release
              </div>
              <div
                style={{ fontSize: 11, color: 'var(--ink4)', marginTop: 2 }}
                data-testid="lien-retention-row"
              >
                {retentionDeadline
                  ? `Due ${fmtDate(retentionDeadline)}${
                    retentionDays != null
                      ? ` · ${humanizeDaysRemaining(retentionDays, retentionDeadline)}`
                      : ''
                  } · Soren fires request day 40`
                  : 'Due 45 days after project completion · Soren fires request day 40'}
              </div>
            </div>
            <span className="chip c-gry">Pending</span>
          </div>
        </div>
      )}

      <div style={{
        background: 'var(--accent-l)', borderRadius: 'var(--rs)',
        padding: '12px 14px', fontSize: 11, color: 'var(--ink3)', lineHeight: 1.55,
      }}>
        <strong style={{ color: 'var(--ink)' }}>How Soren helps:</strong>{' '}
        Auto-drafts the 20-day Pre Lien on project start, monitors completion
        for mechanics lien window, fires retention release at day 40.
      </div>

      <LienDocsList projectId={projectId} />

      <PreLienInstructionsModal
        isOpen={showInstructions}
        onClose={() => setShowInstructions(false)}
        deadline={preLienAlert?.deadline_date ?? null}
        ownerAddress={null}
        lenderAddress={null}
        generalContractorAddress={null}
      />

      <ComingSoonModal
        isOpen={showMailModal}
        title="Mail for me — Coming Soon"
        body="Lob.com certified-mail integration is in development. For now, use the Instructions option to mail the notice via USPS yourself."
        onClose={() => setShowMailModal(false)}
      />
    </div>
  )
}
